import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { fetchModels } from '../services/dataService';
import { useFocusTrap } from '../utils/useFocusManagement';

const ModelSearchDialog = ({ open, onClose, onSelect, title = "Select Model" }) => {
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Focus trap for modal accessibility
  const dialogRef = useFocusTrap(open);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      loadModels();
      // Focus search input when dialog opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = models.filter(model =>
        (model.MODEL_CODE || model.model_code)?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredModels(filtered);
    } else {
      setFilteredModels(models);
    }
    setPage(0);
  }, [searchTerm, models]);

  const loadModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchModels();
      setModels(data);
      setFilteredModels(data);
    } catch (err) {
      setError('Failed to load models');
      console.error('Error loading models:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (model) => {
    onSelect(model.MODEL_CODE || model.model_code);
    handleClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    setPage(0);
    onClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedModels = filteredModels.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="model-search-dialog-title"
      aria-describedby="model-search-dialog-description"
      role="dialog"
      aria-modal="true"
    >
      <div ref={dialogRef}>
        <DialogTitle id="model-search-dialog-title">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {title}
            <IconButton
              aria-label="Close model search dialog"
              onClick={handleClose}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <Close aria-hidden="true" />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent id="model-search-dialog-description">
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Search Models"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by Model Code"
              size="small"
              inputRef={searchInputRef}
              inputProps={{
                'aria-label': 'Search models by code',
                'aria-describedby': 'search-helper-text'
              }}
            />
            <span id="search-helper-text" className="sr-only">
              Type to filter models by code. Use arrow keys to navigate results.
            </span>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} role="alert" aria-live="assertive">
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} role="status" aria-live="polite">
              <CircularProgress aria-label="Loading models" />
              <span className="sr-only">Loading models, please wait</span>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table 
                  stickyHeader 
                  size="small" 
                  aria-label="Models selection table"
                  aria-describedby="table-description"
                >
                  <caption id="table-description" className="sr-only">
                    List of available models. Click or press Enter on a row to select.
                  </caption>
                  <TableHead>
                    <TableRow>
                      <TableCell component="th" scope="col">Model Code</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedModels.map((model, index) => (
                      <TableRow
                        key={`${model.MODEL_ID}-${index}`}
                        hover
                        onClick={() => handleRowClick(model)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleRowClick(model);
                          }
                        }}
                        sx={{ cursor: 'pointer' }}
                        tabIndex={0}
                        role="button"
                        aria-label={`Select model ${model.MODEL_CODE || model.model_code}`}
                      >
                        <TableCell component="th" scope="row">
                          {model.MODEL_CODE || model.model_code}
                        </TableCell>
                      </TableRow>
                    ))}
                    {paginatedModels.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={1} align="center" role="cell">
                          No models found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={filteredModels.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                aria-label="Models table pagination"
              />
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} aria-label="Cancel and close dialog">
            Cancel
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};

export default ModelSearchDialog;